<script lang="ts" context="module">
</script>

<script lang="ts">
  import { isInsideModal } from "$components";
  import { SortOrder } from "$apis/parkito";
  import * as Table from "./types";
  import { derived, writable } from "svelte/store";
  import Checkbox from "../Checkbox.svelte";
  import { log, range } from "$shared/utils/misc";

  export let tableData: Table.Data<any>;
  export let sort: Table.Sort<any> | undefined = undefined;
  export let selectedIds = writable<string[]>([]);
  export let enableSelect: boolean = false;
  export let stickyHeader: boolean = true;

  export let customClass: string = "";
  export let onClick: ((row: number, id: string) => void) | null = null;
  export let disableCursorPointer: boolean = false;

  const inModal = isInsideModal();

  let allChecked = writable<boolean>(false);
  let selectedRows = derived(
    selectedIds,
    (selectedIds: string[]) => {
      return tableData.rows.map((r) => selectedIds.includes(r.id));
    },
  );
  let lastClickedIdx = writable<number | undefined>(undefined);

  let allCheckFirstCycle = true;
  allChecked.subscribe((checked) => {
    // dont run this on the first cycle
    // to not clean out initital values
    if (allCheckFirstCycle) {
      allCheckFirstCycle = false;
      return;
    }
    if (checked) {
      selectIds(tableData.rows.map((r) => r.id));
    } else {
      log.warn("Cleaning selected ids");
      deselectIds(tableData.rows.map((r) => r.id));
    }
  });

  const isNumeric = (type: Table.ColumnType) =>
    type === "number" || type === "currency";

  const headerClicked = (header: Table.Header) => {
    if (!header.sortKey) {
      return;
    }

    // If the same header is clicked twice, reverse order
    if (sort && sort.by === header.sortKey) {
      // @TODO: Figure out a nice way to add reverse to SortOrder
      if (sort.order === "asc") {
        sort.order = SortOrder.DESC;
      } else {
        sort.order = SortOrder.ASC;
      }
    } else if (sort) {
      // if its another header, just swap the key
      sort.by = header.sortKey;
    }
  };

  const deselectIds = (ids: string[]) => {
    const idSet = new Set($selectedIds);
    ids.forEach((id) => idSet.delete(id));
    selectedIds.set([...idSet]);
  };

  const selectIds = (ids: string[]) => {
    const idSet = new Set($selectedIds);
    ids.forEach((id) => idSet.add(id));
    selectedIds.set([...idSet]);
  };

  const idxClicked = (idx: number, shiftPressed: boolean) => {
    if (!shiftPressed) {
      const selected = !$selectedRows[idx];
      if (selected) {
        selectIds([tableData.rows[idx].id]);
      } else {
        deselectIds([tableData.rows[idx].id]);
      }
      $lastClickedIdx = idx;
    } else if ($lastClickedIdx !== undefined) {
      const lastClicked = $lastClickedIdx;
      const selected = !$selectedRows[idx];
      const from = Math.min(lastClicked, idx);
      const to = Math.max(lastClicked, idx);

      const affectedRange = range(from, to + 1);
      const affectedIds = affectedRange.map((row) =>
        tableData.rows[row].id
      );

      if (selected) {
        selectIds(affectedIds);
      } else {
        deselectIds(affectedIds);
      }
      $lastClickedIdx = idx;
    }
  };
</script>

<div class={customClass}>
  {#if enableSelect}
    <div
      class="ml-[1.5rem] text-xs min-w-4"
      class:invisible={$selectedIds.length === 0}
    >
      {$selectedIds.length}
      {#if $selectedIds.length === 1}
        vald
      {:else}
        valda
      {/if}
    </div>
  {/if}

  <table class="w-full table-fixed border-collapse">
    <thead
      class={`
        ${stickyHeader ? "sticky z-10 stickstick" : ""}
        ${inModal ? "top-[0px]" : "top-[50px]"}
        text-red border-b-1 
      `}
    >
      <tr class="px-4">
        {#if enableSelect}
          <th class="check-column">
            <Checkbox
              customClass="checkbox"
              name="row-select-all"
              bind:checked={$allChecked}
            />
          </th>
        {/if}
        {#each tableData.headers as header}
          <th
            class:text-right={isNumeric(header.type) && !header.center}
            class:text-left={!isNumeric(header.type) && !header.center}
            class:text-center={header.center}
            class="whitespace-nowrap"
          >
            <button
              on:click={() => headerClicked(header)}
              disabled={header.sortKey === undefined}
              class="overflow-hidden w-full text-ellipsis"
              class:text-right={isNumeric(header.type) && !header.center}
              class:text-left={!isNumeric(header.type) && !header.center}
              class:text-center={header.center}
              title={header.title}
            >
              {#if               sort && header.sortKey &&
                sort.by === header.sortKey}
                <span class="text-gray-80">
                  {sort.order === SortOrder.ASC ? "↓" : "↑"}
                </span>
              {/if}
              {#if               sort && header.sortKey &&
                sort.by !== header.sortKey}
                <span class="text-grey-40">↕</span>
              {/if}
              <span>{header.title}</span>
            </button>
          </th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each tableData.rows as row, i}
        <tr
          role={onClick ? "button" : undefined}
          class:cursor-pointer={!disableCursorPointer}
          class="
            border-t-[1px] last:border-b-[1px] border-greenGrey-5 hover:bg-[#E3F2F3]
            {$selectedRows[i] ? '!bg-[#CADFE0]' : 'bg-[#F0F8F9]'}
          "
          on:click={(_) => onClick && onClick(i, row.id)}
        >
          {#if enableSelect}
            <td class="check-column">
              <Checkbox
                customClass="checkbox"
                name="row-selector-{i}"
                checked={$selectedRows[i]}
                onClicked={(shiftPressed) => idxClicked(i, shiftPressed)}
              />
            </td>
          {/if}
          <slot prop={row} rowIdx={i} />
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<style>
  /* Td styles is set in app.css due to its declaration in parent component */
  .tab {
    white-space: nowrap; /* Prevent wrapping */
    overflow: hidden; /* Hide overflow */
    text-overflow: ellipsis; /* Optional: show ... */
  }
  .shadow {
    box-shadow: inset -5px 0 5px -5px rgba(0, 0, 0, 0.5);
  }
  .check-column {
    width: min-content;
    box-sizing: content-box;
  }
</style>
